import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/CognitoAuthContext";
import PalliumPageBackground from "../layout/PalliumPageBackground";
import PalliumCard from "../layout/PalliumCard";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { error, isLoading, resetPassword } = useAuth();
  const email = searchParams.get("email");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError(null);
    
    if (password !== confirmPassword) {
      setLocalError("Passwords do not match");
      setIsSubmitting(false);
      return;
    }
    
    if (!email) {
      setLocalError("Email is required");
      setIsSubmitting(false);
      return;
    }
    
    try {
      await resetPassword(email, code, password);
      navigate("/", { replace: true });
    } catch (error) {
      console.error('Reset password error:', error);
      setLocalError(error.message || "Failed to reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input field styles
  const inputBaseStyle = {
    border: '1px solid var(--pallium-border-default)',
    borderRadius: 'var(--pallium-border-radius-md)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-focus)';
    e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-default)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <PalliumPageBackground>
      <PalliumCard centered width="narrow" padding="large">
        <div className="flex justify-center mb-6">
          <img 
            src="/pallium-logo-blue.svg" 
            alt="Pallium Logo" 
            className="h-12 w-auto"
          />
        </div>
        <h2 
          className="text-2xl font-bold text-center mb-6"
          style={{ color: 'var(--pallium-primary-dark-navy)' }}
        >
          Reset Password
        </h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label 
              className="block mb-1 text-sm font-medium"
              style={{ color: 'var(--pallium-text-tertiary)' }}
            >
              New Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-3 focus:outline-none"
              style={inputBaseStyle}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              required
              disabled={isSubmitting || isLoading}
            />
          </div>
          <div>
            <label 
              className="block mb-1 text-sm font-medium"
              style={{ color: 'var(--pallium-text-tertiary)' }}
            >
              Confirm New Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-3 focus:outline-none"
              style={inputBaseStyle}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              required
              disabled={isSubmitting || isLoading}
            />
          </div>
          <div>
            <label 
              className="block mb-1 text-sm font-medium"
              style={{ color: 'var(--pallium-text-tertiary)' }}
            >
              Enter code sent to your email address
            </label>
            <input
              className="w-full px-4 py-3 focus:outline-none"
              style={inputBaseStyle}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              required
              disabled={isSubmitting || isLoading}
            />
          </div>
          {(error || localError) && (
            <div 
              className="p-3 rounded-md text-sm"
              style={{ 
                backgroundColor: 'var(--pallium-alert-error-bg)', 
                color: 'var(--pallium-error-red)',
                border: '1px solid var(--pallium-error-red)'
              }}
            >
              {localError || error}
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full py-3 rounded-md text-white font-medium transition-colors duration-200 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: isSubmitting || isLoading ? 'var(--pallium-button-primary-disabled)' : 'var(--pallium-button-primary-bg)',
              borderRadius: 'var(--pallium-border-radius-md)'
            }}
            onMouseOver={(e) => { if (!isSubmitting && !isLoading) e.target.style.backgroundColor = 'var(--pallium-button-primary-hover)'; }}
            onMouseOut={(e) => { if (!isSubmitting && !isLoading) e.target.style.backgroundColor = 'var(--pallium-button-primary-bg)'; }}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
          <div className="text-center">
            <button 
              type="button"
              className="text-sm cursor-pointer hover:underline bg-transparent border-none p-0"
              style={{ color: 'var(--pallium-link-default)' }}
              onClick={() => navigate('/')}
              onMouseOver={(e) => e.target.style.color = 'var(--pallium-link-hover)'}
              onMouseOut={(e) => e.target.style.color = 'var(--pallium-link-default)'}
            >
              Back to Login
            </button>
          </div>
        </form>
      </PalliumCard>
    </PalliumPageBackground>
  );
};

export default ResetPassword;
